import { Link, useParams } from 'react-router';
import { useFetchRootCategories } from '../../lib/fetches';
import Tab from './Tab';
import { useEffect, useRef, useState } from 'react';
import Skeleton from '../Skeleton';
import { Color, LectureCategory } from '../../lib/types';
import { cn } from '../../lib/helpers.ts';

interface TabItem {
    name: string;
    slug: string;
    color: Color | 'product';
}

export default function CategoriesTabs() {
    const { data: categories, isLoading } = useFetchRootCategories();

    const categoriesTabs: TabItem[] =
        categories
            ?.filter(({ is_locked }) => !is_locked)
            .map(({ name, color, slug }: LectureCategory): TabItem => {
                return { name, color, slug };
            }) ?? [];

    const tabs: TabItem[] = [
        {
            name: 'Vše',
            color: 'product',
            slug: '',
        },
        // Categories from API,
        ...categoriesTabs,
    ];

    const { category = '' } = useParams();

    const activeTabRef = useRef<HTMLButtonElement>(null);

    const tabDimensionsInitialState = {
        left: -12,
        top: 0,
        width: 61,
        height: 42,
    };
    const [tabDimensions, setTabDimensions] = useState(
        tabDimensionsInitialState
    );

    useEffect(() => {
        const boundingRect = activeTabRef?.current?.getBoundingClientRect();
        setTabDimensions({
            left:
                activeTabRef?.current?.offsetLeft ??
                tabDimensionsInitialState.left,
            top:
                activeTabRef?.current?.offsetTop ??
                tabDimensionsInitialState.top,
            width: boundingRect?.width ?? tabDimensionsInitialState.width,
            height: boundingRect?.height ?? tabDimensionsInitialState.height,
        });
    }, [isLoading, categories, category, activeTabRef]);

    const colorVariants = {
        blue: 'bg-blue-500',
        green: 'bg-green-500',
        orange: 'bg-orange-500',
        red: 'bg-red-500',
        turquoise: 'bg-turquoise-500',
        purple: 'bg-purple-500',
        rose: 'bg-rose-500',
        product: 'bg-product-500',
        transparent: 'bg-transparent',
    };

    const activeItem = tabs.filter((item) => item.slug === category);
    const activeItemColor = activeItem.length
        ? activeItem[0].color
        : 'transparent';

    return (
        <div
            role="tablist"
            className="relative flex flex-wrap items-center justify-start space-x-3"
        >
            <div
                className={cn(
                    'absolute inset-0 h-10 rounded-xl',
                    'transition-all',
                    colorVariants[activeItemColor],
                    activeItemColor !== 'transparent' && 'shadow-sm'
                )}
                style={{
                    transform: `translateX(${tabDimensions.left}px) translateY(${tabDimensions.top}px)`,
                    width: `${tabDimensions.width}px`,
                }}
            />

            {tabs.map(({ name, slug, color }) => {
                const isActive = slug === category;

                return (
                    <span key={slug} ref={isActive ? activeTabRef : undefined}>
                        <Link to={`/cviceni/${slug}`}>
                            <Tab color={color} active={isActive}>
                                {name}
                            </Tab>
                        </Link>
                    </span>
                );
            })}

            {isLoading && (
                <Skeleton count={3} className="h-10 w-32 rounded-xl" />
            )}
        </div>
    );
}
